// La portada del Catálogo en dos pasos (ADR-2026-10-13-catalogo-con-el-agente,
// ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa):
//  1. Elegir los productos, con el selector de los modos de pantalla completa (el de Ambiente
//     completo): buscador y grilla. El pie es el del selector del agente ("Elegí qué agregar"):
//     las miniaturas de lo elegido a la izquierda —cada foto tocada VUELA hasta la suya— y
//     "Siguiente" a la derecha (ADR-2026-10-14-el-pie-de-elegir-productos-es-el-del-agente).
//  2. El agente a pantalla completa (`IntroDelCatalogo`): Cumbre pregunta cómo se quiere el
//     catálogo. Mandar crea la sesión (un chat de tipo `catalogo`), guarda el plan vacío con los
//     productos y sus datos, carga ese chat y abre la sesión, que sigue con la conversación a
//     pantalla completa hasta que el agente arma el catálogo
//     (ADR-2026-10-14-el-catalogo-conversa-a-pantalla-completa-hasta-armar). La sesión le manda el
//     pedido al agente.
import { ArrowRight, ChevronDown, Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useChatAtlas } from '../../../../chat-atlas/ChatAtlasContext';
import { MiniaturasElegidas } from '../../../../chat-atlas/ui/MiniaturasElegidas';
import { EncabezadoPantalla } from '../../../../components/PantallaChrome';
import type { ChatAttachment } from '../../agentAttachments';
import { TIPO_CHAT_CATALOGO, createChat, listSesionesCatalogo, type SesionDeCatalogoResumen } from '../../agentChatApi';
import { useAgentEnv } from '../../agentEnvContext';
import { useDebajoDeMd } from '../../../../hooks/useEsMobile';
import { useSidebar } from '../../../../context/SidebarContext';
import { PickProductPanel } from '../../components/PickProductPanel';
import { estiloDelMarcoTelefono, MARCO_TELEFONO } from './marcoDelTelefono';
import { SubirProductoDrawer } from '../../components/SubirProductoDrawer';
import { fetchFichaPorVariante, fetchMedidasPorVariante } from '../../medidasApi';
import type { MockProductV2 } from '../../mockData';
import { IntroDelCatalogo } from './IntroDelCatalogo';
import { MAX_PRODUCTOS, planVacio, type PlanDeCatalogo, type ProductoParaElPlan, type ReferenciaDelPlan } from './plan';
import { guardarPlan } from './planGuardado';
import { RUTA_CATALOGO, rutaDeSesion } from './rutas';
import './transicionDelCatalogo.css';
import { volarAlPie } from './vueloAlPie';

/** Lo que la portada le deja a la sesión para que mande el primer mensaje al agente. */
export interface PrimerMensaje {
  texto: string;
  adjuntos: ChatAttachment[];
}

/** El `location.state` con que la portada abre la sesión: el pedido y el plan recién guardado
 *  (así la sesión dibuja el catálogo en el primer frame, sin esperar a leerlo). */
export interface EstadoDeLaPortada {
  primerMensaje: PrimerMensaje;
  plan: PlanDeCatalogo;
}

const referenciasDe = (adjuntos: ChatAttachment[]): ReferenciaDelPlan[] =>
  adjuntos
    .filter((a): a is Extract<ChatAttachment, { kind: 'image' }> => a.kind === 'image')
    .map((a, i) => ({ id: a.id, etiqueta: `Referencia ${i + 1}`, storagePath: a.storagePath, ...(a.bucket ? { bucket: a.bucket } : {}) }));

/** Lo que la barra flotante tapa del borde de abajo: el aire que la grilla deja al final. */
const ESPACIO_DE_LA_BARRA = 136;
/** Lo que tarda la barra en aparecer (la `duration-300` de su transición). */
const ENTRADA_DE_LA_BARRA_MS = 300;

const pideMenosMovimiento = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

const fechaDeSesion = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * "Mis catálogos": la lista va en un PORTAL al body, con posición fija debajo del botón. Adentro
 * de la cabecera del selector de productos quedaba en el mismo contexto de apilado que su barra
 * de búsqueda pegajosa, que le pasaba por encima.
 */
/**
 * Cuántos van de los 100: un anillo que se llena y el número. Cerca del tope se pone ámbar y,
 * lleno, dice que es el máximo (ADR-2026-10-14-el-tope-de-productos-se-ve).
 */
const ContadorDelTope: React.FC<{ cantidad: number }> = ({ cantidad }) => {
  const parte = Math.min(1, cantidad / MAX_PRODUCTOS);
  const lleno = cantidad >= MAX_PRODUCTOS;
  const cerca = !lleno && cantidad >= MAX_PRODUCTOS * 0.9;
  const color = lleno ? '#B54708' : cerca ? '#DC6803' : 'rgb(var(--brand-primary-rgb))';
  const r = 8;
  const largo = 2 * Math.PI * r;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-2 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold transition-colors ${
        lleno ? 'bg-[#FFF4E5] text-[#B54708]' : cerca ? 'bg-[#FFF8EB] text-[#B54708]' : 'text-[#02062B]/[0.6]'
      }`}
      title={`Hasta ${MAX_PRODUCTOS} productos por catálogo`}
    >
      <svg viewBox="0 0 20 20" className="h-5 w-5 -rotate-90" aria-hidden="true">
        <circle cx="10" cy="10" r={r} fill="none" stroke="currentColor" strokeOpacity={0.15} strokeWidth="2.5" />
        <circle
          cx="10"
          cy="10"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={largo}
          strokeDashoffset={largo * (1 - parte)}
          style={{ transition: 'stroke-dashoffset .3s ease-out, stroke .2s' }}
        />
      </svg>
      {lleno ? `${MAX_PRODUCTOS} de ${MAX_PRODUCTOS}: el máximo` : `${cantidad} de ${MAX_PRODUCTOS}`}
    </span>
  );
};

const MisCatalogos: React.FC<{ sesiones: SesionDeCatalogoResumen[]; onAbrir: (id: string) => void }> = ({ sesiones, onAbrir }) => {
  const botonRef = useRef<HTMLButtonElement>(null);
  const [lugar, setLugar] = useState<{ top: number; right: number } | null>(null);
  const cerrar = useCallback(() => setLugar(null), []);
  useEffect(() => {
    if (!lugar) return;
    const alTeclado = (e: KeyboardEvent) => e.key === 'Escape' && cerrar();
    window.addEventListener('resize', cerrar);
    window.addEventListener('keydown', alTeclado);
    return () => {
      window.removeEventListener('resize', cerrar);
      window.removeEventListener('keydown', alTeclado);
    };
  }, [lugar, cerrar]);
  if (sesiones.length === 0) return null;
  const alternar = () => {
    if (lugar) return cerrar();
    const r = botonRef.current?.getBoundingClientRect();
    if (r) setLugar({ top: r.bottom + 8, right: window.innerWidth - r.right });
  };
  return (
    <div className="shrink-0">
      <button
        ref={botonRef}
        type="button"
        onClick={alternar}
        aria-expanded={!!lugar}
        className="inline-flex items-center gap-1.5 rounded-full bg-white py-2 px-4 text-[14px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.06] transition-colors hover:bg-black/[0.03]"
      >
        Mis catálogos
        <ChevronDown className={`h-4 w-4 transition-transform ${lugar ? 'rotate-180' : ''}`} strokeWidth={2} aria-hidden="true" />
      </button>
      {lugar &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[200]" onClick={cerrar} aria-hidden="true" />
            <ul
              className="fixed z-[201] max-h-[min(420px,calc(100vh-120px))] w-[300px] overflow-y-auto rounded-2xl bg-white p-2 shadow-lg ring-1 ring-black/[0.06]"
              style={{ top: lugar.top, right: lugar.right }}
            >
              {sesiones.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      cerrar();
                      onAbrir(s.id);
                    }}
                    className="flex w-full flex-col items-start rounded-xl px-3 py-2 text-left transition-colors hover:bg-black/[0.04]"
                  >
                    <span className="w-full truncate text-[14px] font-semibold text-brand-black">{s.titulo}</span>
                    <span className="text-[12px] text-[#3C3C43]/60">{fechaDeSesion(s.actualizadoEn)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>,
          document.body,
        )}
    </div>
  );
};

export const PortadaDeCatalogo: React.FC<{
  rubro: string | null;
  /** Se dio de alta un producto: el host vuelve a traer el catálogo de la marca. */
  onProductosNuevos?: () => void;
}> = ({ rubro, onProductosNuevos }) => {
  const navigate = useNavigate();
  const { userId, companyId, brandId } = useAgentEnv();
  // En el teléfono la grilla elige sola 2 o 3 columnas y la tarjeta ocupa toda la pantalla
  // (ADR-2026-10-15-el-catalogo-en-el-telefono).
  const esTelefono = useDebajoDeMd();
  // La tarjeta "Subir nuevo producto" de la grilla abre el alta, como en los otros modos.
  const [subirProductoAbierto, setSubirProductoAbierto] = useState(false);
  const { cargarChat } = useChatAtlas();
  const [paso, setPaso] = useState<'productos' | 'pedido'>('productos');
  // En el teléfono el botón de atrás del shell retrocede DE PASO, como en Ambiente completo: de la
  // pregunta vuelve a los productos (sin perder lo elegido) y sólo desde la grilla sale del modo.
  // Sin esto hacía `history.back()` y la pregunta, que es un paso de la misma ruta, sacaba del
  // Catálogo (ADR-2026-10-15-el-catalogo-en-el-telefono-3).
  const { setMobileBackOverride } = useSidebar();
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'pedido') setPaso('productos');
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
  }, [paso, setMobileBackOverride, navigate]);
  const [elegidos, setElegidos] = useState<MockProductV2[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sesiones, setSesiones] = useState<SesionDeCatalogoResumen[]>([]);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    void listSesionesCatalogo().then((s) => {
      if (vivo.current) setSesiones(s);
    });
    return () => {
      vivo.current = false;
    };
  }, []);

  // ── El pie: lo elegido, con el vuelo de cada foto que se toca ─────────────
  const grillaRef = useRef<HTMLDivElement>(null);
  const pieRef = useRef<HTMLDivElement>(null);
  const [clavePanel, setClavePanel] = useState(0);
  const previosRef = useRef<Set<string> | null>(null);
  const vuelos = useRef(new Set<() => void>());
  useEffect(() => {
    const enCurso = vuelos.current;
    return () => {
      for (const cancelar of enCurso) cancelar();
      enCurso.clear();
    };
  }, []);
  const cambiarSeleccion = useCallback((lista: MockProductV2[]) => {
    const previos = previosRef.current;
    previosRef.current = new Set(lista.map((p) => p.id));
    setElegidos(lista);
    // El primer aviso del panel (al montar) trae lo que ya estaba: eso no vuela.
    if (!previos) return;
    // Con la barra escondida (no había nada elegido), el vuelo espera a que termine de entrar.
    const espera = previos.size === 0 ? ENTRADA_DE_LA_BARRA_MS : 0;
    for (const p of lista) {
      if (previos.has(p.id)) continue;
      const cancelar = volarAlPie(p.id, grillaRef.current, pieRef.current, espera);
      vuelos.current.add(cancelar);
      window.setTimeout(() => vuelos.current.delete(cancelar), 1_000 + espera);
    }
  }, []);
  /** La X de una miniatura: es tocar su tarjeta en la grilla (el mismo camino que elegirla). Si
   *  el buscador la está escondiendo, el panel se vuelve a montar sin ella. */
  const quitarElegido = useCallback(
    (id: string) => {
      const tarjeta = grillaRef.current?.querySelector<HTMLElement>(`[data-tira-tile="${CSS.escape(id)}"]`);
      if (tarjeta) {
        tarjeta.click();
        return;
      }
      const restantes = elegidos.filter((p) => p.id !== id);
      previosRef.current = null;
      setElegidos(restantes);
      setClavePanel((n) => n + 1);
    },
    [elegidos],
  );

  // ── El tope de 100 (ADR-2026-10-14-el-tope-de-productos-se-ve) ─────────────
  // Con el tope lleno, tocar un producto que no está elegido no lo suma: el toque se ataja en la
  // captura (antes de que llegue a la tarjeta), la barra se sacude y avisa arriba. Las tarjetas
  // que ya no entran se ven apagadas.
  const alTope = elegidos.length >= MAX_PRODUCTOS;
  const [avisoDeTope, setAvisoDeTope] = useState(0);
  useEffect(() => {
    if (!avisoDeTope) return;
    pieRef.current?.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(7px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(2px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 380, easing: 'ease-out' },
    );
    const t = window.setTimeout(() => setAvisoDeTope(0), 2_800);
    return () => window.clearTimeout(t);
  }, [avisoDeTope]);
  const atajarAlTope = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!alTope) return;
      if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
      const tarjeta = (e.target as HTMLElement).closest<HTMLElement>('[data-tira-tile]');
      const id = tarjeta?.getAttribute('data-tira-tile');
      if (!id || elegidos.some((p) => p.id === id)) return;
      e.preventDefault();
      e.stopPropagation();
      setAvisoDeTope((n) => n + 1);
    },
    [alTope, elegidos],
  );
  /** Con el tope lleno, las tarjetas que no están elegidas se apagan (las elegidas no). */
  const estiloDelTope = alTope
    ? `[data-grilla-catalogo] [data-tira-tile]{opacity:.4;filter:grayscale(.5);cursor:not-allowed;transition:opacity .2s,filter .2s}` +
      elegidos.map((p) => `[data-grilla-catalogo] [data-tira-tile="${CSS.escape(p.id)}"]`).join(',') +
      `{opacity:1;filter:none;cursor:pointer}`
    : '';

  const motivoBloqueo =
    elegidos.length === 0
      ? 'Elegí al menos un producto.'
      : elegidos.length > MAX_PRODUCTOS
        ? `Hasta ${MAX_PRODUCTOS} productos por catálogo (elegiste ${elegidos.length}).`
        : null;

  const enviar = useCallback(
    async (texto: string, adjuntos: ChatAttachment[]) => {
      if (!userId || !companyId || motivoBloqueo || enviando) return;
      setEnviando(true);
      setError(null);
      try {
        const ids = elegidos.map((p) => p.id);
        const [medidas, fichas] = await Promise.all([fetchMedidasPorVariante(ids), fetchFichaPorVariante(ids)]);
        const productos: ProductoParaElPlan[] = elegidos.map((p) => ({
          varianteId: p.id,
          nombre: p.name,
          ...(p.imageUrl ? { portadaUrl: p.imageUrl } : {}),
          medidas: medidas[p.id] ?? null,
          ficha: fichas[p.id] ?? { campos: [] },
        }));
        const plan = planVacio(productos, referenciasDe(adjuntos), texto);
        const id = await createChat({ userId, companyId, title: plan.titulo, messages: [], tipo: TIPO_CHAT_CATALOGO });
        if (!id) throw new Error('No se pudo crear la sesión.');
        await guardarPlan(id, plan);
        // El chat se carga ANTES de abrir la sesión: si no, la conversación mostraría un instante
        // la que estaba abierta antes.
        await cargarChat(id);
        if (!vivo.current) return;
        const estado: EstadoDeLaPortada = { primerMensaje: { texto, adjuntos }, plan };
        const abrir = () => navigate(rutaDeSesion(id), { state: estado });
        const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
        if (doc.startViewTransition && !pideMenosMovimiento()) doc.startViewTransition(() => flushSync(abrir));
        else abrir();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo abrir la sesión. Probá de nuevo.');
        setEnviando(false);
      }
    },
    [userId, companyId, motivoBloqueo, enviando, elegidos, cargarChat, navigate],
  );

  if (paso === 'pedido') {
    return <IntroDelCatalogo productos={elegidos} enviando={enviando} error={error} onEnviar={(t, a) => void enviar(t, a)} onVolver={() => setPaso('productos')} />;
  }

  // El paso 1 es la columna de productos de los modos de pantalla completa (Ambiente completo):
  // migas y título adentro y la grilla; el pie es propio (el del selector del agente).
  // Migas y el título grande con su separación antes de la grilla
  // (ADR-2026-10-14-elegir-productos-con-aire).
  const cabecera = (
    <div className="flex flex-wrap items-end justify-between gap-3 px-4 pt-4 pb-3 md:gap-6 md:px-6 md:pt-6">
      <div>
        <EncabezadoPantalla
          migas={[{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: 'Catálogo', onClick: () => navigate(RUTA_CATALOGO) }]}
          // En el teléfono las migas no van: el botón de atrás del shell es la salida.
          className="!px-0 !pt-0 !pb-4 hidden md:block"
        />
        <h1 className="text-[22px] font-semibold md:text-[28px] leading-tight tracking-[-0.01em] text-brand-black">Elegí los productos para el catálogo</h1>
      </div>
      <MisCatalogos sesiones={sesiones} onAbrir={(id) => navigate(rutaDeSesion(id))} />
    </div>
  );
  return (
    // La geometría de las pantallas de modo (Ambiente completo, Automático): el gris de la app
    // —el mismo de detrás de la sidebar— con 52 px de aire, y la grilla en UNA tarjeta blanca
    // (ADR-2026-10-14-elegir-productos-va-en-la-tarjeta-de-los-modos).
    <div className={`flex h-full min-h-0 flex-col bg-surface-mobile md:px-[52px] md:py-[52px] ${MARCO_TELEFONO}`} style={estiloDelMarcoTelefono(esTelefono)}>
      <div className="relative mx-auto flex min-h-0 w-full max-w-[1320px] flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:shadow-sm md:ring-1 md:ring-black/[0.04]">
        {estiloDelTope && <style>{estiloDelTope}</style>}
        <div
          ref={grillaRef}
          data-grilla-catalogo=""
          className="relative flex min-h-0 flex-1 flex-col"
          onClickCapture={atajarAlTope}
          onKeyDownCapture={atajarAlTope}
        >
          <PickProductPanel
            key={clavePanel}
            variant="desktop"
            hideBack
            hideFooter
            rubro={rubro}
            initialSelectedIds={elegidos.map((p) => p.id)}
            hideHeader
            cabecera={cabecera}
            nombreSobreLaFoto
            columnasFijas={esTelefono ? undefined : 5}
            ocultarBloqueSubida
            onSubirProducto={() => setSubirProductoAbierto(true)}
            onSelectionChange={cambiarSeleccion}
            espacioAlPie={ESPACIO_DE_LA_BARRA}
            // Al scrollear se pliegan el título y el recuento: arriba queda sólo el buscador.
            plegarAlScrollear
            onConfirm={() => setPaso('pedido')}
            onBack={() => navigate('/interfaz-v2')}
          />
        </div>
        {/* El pie del selector del agente, FLOTANDO sobre el borde de abajo de la grilla (no una
            franja blanca a lo ancho): lo elegido a la izquierda y el botón a la derecha. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 px-3 pb-[max(env(safe-area-inset-bottom),12px)] md:px-12 md:pb-6">
          {avisoDeTope > 0 && (
            <p
              key={avisoDeTope}
              role="status"
              className="animate-fade-in pointer-events-auto inline-flex items-center gap-2 rounded-full bg-brand-black py-2 px-4 text-[13px] font-medium text-white shadow-lg"
            >
              <Info className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
              Llegaste a {MAX_PRODUCTOS} productos, el máximo por catálogo. Sacá uno para sumar otro.
            </p>
          )}
          {/* Escondida hasta el primer producto: entra subiendo y con un fundido, y sale igual
              al sacar el último. Sigue montada para que las miniaturas hagan su salida. */}
          <div
            ref={pieRef}
            aria-hidden={elegidos.length === 0}
            className={`flex w-full max-w-[1040px] items-center gap-3 rounded-[26px] bg-white/90 py-3 pl-4 pr-3 md:gap-4 md:py-3.5 md:pl-6 md:pr-3.5 shadow-[0_16px_48px_-14px_rgba(2,6,43,0.3),0_2px_8px_rgba(2,6,43,0.06)] ring-1 ring-black/[0.06] backdrop-blur-md transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
              elegidos.length > 0 ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-5 opacity-0'
            }`}
            >
            <MiniaturasElegidas
              items={elegidos.map((p) => ({ id: p.id, url: p.imageUrl ?? null, nombre: p.name }))}
              onQuitar={quitarElegido}
              className="flex flex-1"
              tamano={esTelefono ? 'normal' : 'grande'}
            />
            {elegidos.length > 0 && <ContadorDelTope cantidad={elegidos.length} />}
            <button
              type="button"
              onClick={() => setPaso('pedido')}
              disabled={!!motivoBloqueo}
              tabIndex={elegidos.length === 0 ? -1 : undefined}
              title={motivoBloqueo ?? undefined}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-primary py-3 px-5 text-[15px] font-semibold text-white md:py-3.5 md:px-7 md:text-[16px] transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              Siguiente
              <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      {/* Alta de producto desde la tarjeta "Subir nuevo producto" de la grilla: el mismo drawer
          que Ambiente completo; al terminar, la pantalla vuelve a traer los productos. */}
      <SubirProductoDrawer
        open={subirProductoAbierto}
        onClose={() => setSubirProductoAbierto(false)}
        userCompanyId={companyId}
        brandId={brandId ?? null}
        rubro={rubro}
        onProductsRefreshed={() => onProductosNuevos?.()}
      />
    </div>
  );
};
